import { DatePipe } from '@angular/common';
import { Component, Input, OnInit, Output, EventEmitter, AfterViewInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RefAddressModel } from 'src/app/model/refAddressModel.model';
import { TemplateAttributeModel } from 'src/app/model/templateattributemodel.model';
import { AccelaControl, AccelaControlType } from 'src/app/modules/shared/model/accelaControl.model';
import { AddressModel } from 'src/app/modules/shared/model/addressModel.model';
import { Column } from 'src/app/modules/shared/model/column.model';
import { ExpressionResponse } from 'src/app/modules/shared/model/expressions.model';
import { AAExpressionService } from 'src/app/modules/shared/services/aaexpression-service.service';
import { PageLayoutService } from 'src/app/modules/shared/services/page-layout.service';
import { APOEntityType } from 'src/app/repo/contact-process-type.repo';
import { FlagEnum, PageLayoutMode } from 'src/app/repo/enum.repo';
import { GFilterViewIdRepo, GFilterViewPermissionLevel } from 'src/app/repo/GFilterViewIds.repo';
import { TemplateType } from 'src/app/repo/template-entity-type.repo';
import { isStringNullOrWhiteSpace } from 'src/app/util/StringUtil';
import { APOSearchService } from '../../services/aposearch-service.service';
import { convertAddressModeltoRefAddressModel } from '../../util/apo.util';
import { SearchResultsPanelComponent } from '../apo-search-results-panel/apo-search-results-panel.component';

@Component({
  selector: 'edit-address-form',
  templateUrl: './edit-address-form.component.html',
  styleUrls: ['./edit-address-form.component.less']
})
export class EditAddressFormComponent implements OnInit, AfterViewInit {

  @Input() model: AddressModel;
  @Input() module: string;
  @Input() mode: PageLayoutMode;
  @Input() addressType = '';
  @Input() addressMax: number;

  @Output() saveChanges = new EventEmitter<AddressModel[]>();
  @Output() closeDialog = new EventEmitter<boolean>();
  @Output() uiBlock = new EventEmitter<boolean>();

  @ViewChild('searchResultsPanel') searchResultsPanelComponent: SearchResultsPanelComponent;

  addressForm: FormGroup;
  controlAddrModelMap: Map<string, string>;

  onLoadExpression: ExpressionResponse;
  onSubmitExpression: ExpressionResponse;
  onExecuteExpressions: ExpressionResponse[];
  executeFields = new Array<string>();

  originalAPOTemplatesdata: any;
  pageLayoutModes = PageLayoutMode;
  processing = false;

  searchResults: RefAddressModel[];
  editTermsText: string;
  showSearchResults: boolean;
  searchColumns: Array<Column> = [
    { field: 'city', header: $localize`City` },
    { field: 'state', header: $localize`State` },
    { field: 'zip', header: $localize`Zip` },
  ];

  readonly viewId = GFilterViewIdRepo.AddressEdit;
  readonly permissionLevel = GFilterViewPermissionLevel.APO;
  readonly permissionValue = APOEntityType.Address;

  protected destroyActions = new Subject<boolean>();

  constructor(private formBuilder: FormBuilder, private messageService: MessageService, private datePipe: DatePipe,
    private expressionService: AAExpressionService, private pageLayoutService: PageLayoutService,
    private apoSearchService: APOSearchService) {
    this.addressForm = this.formBuilder.group({});
    this.populateControlAddrModelMap();
    this.createAddressFormInstance();
  }

  ngOnInit(): void {
    this.processing = true;
    this.uiBlock.emit(true);

    this.fetchExpressions();
  }

  ngAfterViewInit(): void {
  }

  /* Data Setup */
  populateControlAddrModelMap() {
    this.controlAddrModelMap = new Map<string, string>();
    this.controlAddrModelMap.set('ddlCountry', 'country');
    this.controlAddrModelMap.set('txtStreetNo', 'houseNumberStart');
    this.controlAddrModelMap.set('txtStartFraction', 'houseFractionStart');
    this.controlAddrModelMap.set('txtStreetEnd', 'houseNumberEnd');
    this.controlAddrModelMap.set('txtEndFraction', 'houseFractionEnd');
    this.controlAddrModelMap.set('ddlStreetDirection', 'streetDirection');
    this.controlAddrModelMap.set('txtPrefix', 'streetPrefix');
    this.controlAddrModelMap.set('txtStreetName', 'streetName');
    this.controlAddrModelMap.set('ddlStreetSuffix', 'streetSuffix');
    this.controlAddrModelMap.set('ddlStreetSuffixDirection', 'streetSuffixDirection');
    this.controlAddrModelMap.set('ddlUnitType', 'unitType');
    this.controlAddrModelMap.set('txtUnitNo', 'unitStart');
    this.controlAddrModelMap.set('txtUnitEnd', 'unitEnd');
    this.controlAddrModelMap.set('txtSecondaryRoad', 'secondaryRoad');
    this.controlAddrModelMap.set('txtSecondaryRoadNo', 'secondaryRoadNumber');
    this.controlAddrModelMap.set('txtNeighborhoodP', 'neighborhoodPrefix');
    this.controlAddrModelMap.set('txtNeighborhood', 'neighborhood');
    this.controlAddrModelMap.set('txtDescription', 'addressDescription');
    this.controlAddrModelMap.set('txtDistance', 'distance');
    this.controlAddrModelMap.set('txtXCoordinator', 'XCoordinator');
    this.controlAddrModelMap.set('txtYCoordinator', 'YCoordinator');
    this.controlAddrModelMap.set('txtInspectionDP', 'inspectionDistrictPrefix');
    this.controlAddrModelMap.set('txtInspectionD', 'inspectionDistrict');
    this.controlAddrModelMap.set('txtCity', 'city');
    this.controlAddrModelMap.set('txtCounty', 'county');
    this.controlAddrModelMap.set('txtState', 'state');
    this.controlAddrModelMap.set('txtZip', 'zip');
    this.controlAddrModelMap.set('txtStreetAddress', 'fullAddress');
    this.controlAddrModelMap.set('txtAddressLine1', 'addressLine1');
    this.controlAddrModelMap.set('txtAddressLine2', 'addressLine2');
    this.controlAddrModelMap.set('ddlAddressTypeFlag', 'addressType');
    this.controlAddrModelMap.set('addressTypeFlag', 'addressTypeFlag');
    this.controlAddrModelMap.set('txtLevelPrefix', 'levelPrefix');
    this.controlAddrModelMap.set('txtLevelNbrStart', 'levelNumberStart');
    this.controlAddrModelMap.set('txtLevelNbrEnd', 'levelNumberEnd');
    this.controlAddrModelMap.set('txtHouseAlphaStart', 'houseNumberAlphaStart');
    this.controlAddrModelMap.set('txtHouseAlphaEnd', 'houseNumberAlphaEnd');
    this.controlAddrModelMap.set('txtStreetNameStart', 'streetNameStart');
    this.controlAddrModelMap.set('txtStreetNameEnd', 'streetNameEnd');
    this.controlAddrModelMap.set('txtCrossStreetNameStart', 'crossStreetNameStart');
    this.controlAddrModelMap.set('txtCrossStreetNameEnd', 'crossStreetNameEnd');
    this.controlAddrModelMap.set('txtAddressUID', 'UID');
  }

  createAddressFormInstance() {
    this.processing = true;
    this.uiBlock.emit(true);
    this.addressForm = this.formBuilder.group({});
    this.pageLayoutService.getDynamicFormData(this.viewId, this.module, this.permissionLevel, this.permissionValue, 'anonymous')
      .pipe(takeUntil(this.destroyActions))
      .subscribe((resp => {
        this.processing = true;
        resp.forEach(controlData => {
          if (this.mode === PageLayoutMode.ADMIN || controlData.display) {
            const abstractControl = new FormControl(controlData.value);
            this.pageLayoutService.processAccelaControl(controlData, abstractControl);
            if (this.model) {
              if (controlData.templateField || this.isTemplateField(controlData.name)) {
                abstractControl.patchValue(this.getTemplateAtrributeVal(controlData.name));
              } else {
                abstractControl.patchValue(this.model[this.controlAddrModelMap.get(controlData.name)]);
              }
            } else {
              abstractControl.patchValue(null);
            }

            if (controlData.display && (controlData.type !== AccelaControlType.Separator)) {
              this.addressForm.addControl(controlData.name, abstractControl);
            }

          }
        });
        this.handleOnLoadExpression();

        if (this.mode !== PageLayoutMode.ADMIN) {
          this.processing = true;
          this.uiBlock.emit(true);

          this.expressionService
            .getExpressionJsScriptForExecute(this.viewId, this.module, this.permissionLevel, this.permissionValue, 'anonymous')
            .pipe(takeUntil(this.destroyActions))
            .subscribe(res => {
              if (res && res.length > 0) {
                this.onExecuteExpressions = res;
                res.forEach(data => {
                  Object.keys(data.inputFields).forEach(fieldSet => {
                    data.inputFields[fieldSet].forEach(field => {
                      const inputField = this.addressForm.get(field);
                      if (inputField && !(this.executeFields.findIndex(str => str === field) >= 0)) {
                        this.executeFields.push(field);
                      }
                    });
                  });
                });
              }
              this.processing = false;
              this.uiBlock.emit(false);
            }, err => {
              this.processing = false;
              this.uiBlock.emit(false);
              throw err;
            });
        }

      })
        , error => {
          this.messageService.clear();
          this.messageService.add({
            severity: 'error',
            summary: error,
            closable: false
          });
          this.processing = false;
          this.uiBlock.emit(false);
        });
    this.getOriginalAPOTemplatesdata();
  }

  updateAddressFormInstance(address: AddressModel) {
    this.model = address;

    Object.keys(this.addressForm.controls).forEach(key => {
      if (this.isTemplateField(key)) {
        this.addressForm.controls[key].patchValue(this.getTemplateAtrributeVal(key));
      } else {
        if (this.addressForm.controls[key]) {
          this.addressForm.controls[key].patchValue(this.model[this.controlAddrModelMap.get(key)]);
        }
      }

    });
    this.handleOnLoadExpression();

  }

  /* Expression Handling */
  fetchExpressions() {
    //Expression loading
    this.expressionService
      .getExpressionJsScriptOnLoad(this.viewId, this.module, this.permissionLevel, this.permissionValue, 'anonymous')
      .pipe(takeUntil(this.destroyActions))
      .subscribe(res => {
        this.onLoadExpression = res;
        this.processing = false;
        this.uiBlock.emit(false);
      }, err => {
        this.processing = false;
        this.uiBlock.emit(false);
        throw err;
      });

    this.expressionService
      .getExpressionJsScriptOnSubmit(this.viewId, this.module, this.permissionLevel, this.permissionValue, 'anonymous')
      .pipe(takeUntil(this.destroyActions))
      .subscribe(res => {
        this.onSubmitExpression = res;
      });
  }

  invokeExpressionForExecute(event: AbstractControl) {
    if (this.executeFields.findIndex(str => str === event.controlData.name) >= 0) {
      this.onExecuteExpressions.forEach(data => {
        this.executeFields.forEach(field => {
          const inputField = this.addressForm.get(field);
          if (inputField) {
            const ctrlData = inputField.controlData;
            ctrlData.value = inputField.value;
            data.inputFieldProperties[ctrlData.name] = inputField.controlData;
          }
        });
        this.expressionService.invokeExpression(data, this.addressForm, null).subscribe();

      });
    }
  }

  handleOnLoadExpression() {
    if (this.onLoadExpression) {
      this.expressionService
        .invokeExpression(this.onLoadExpression, this.addressForm)
        .pipe(takeUntil(this.destroyActions))
        .subscribe(res => {
          this.processing = false;
          this.uiBlock.emit(false);
        }, err => {
          this.processing = false;
          this.uiBlock.emit(false);
          throw err;
        });
    } else {
      this.processing = false;
      this.uiBlock.emit(false);
    }
  }

  /* Template Handling */
  getOriginalAPOTemplatesdata() {
    this.pageLayoutService.getAPOTemplateData(TemplateType.CAP_ADDRESS).then((res) => {
      if (res) {
        this.originalAPOTemplatesdata = JSON.parse(res.toString());
      }
    });
  }

  getTemplateAtrributeVal(fieldname: string): string | Date {
    let fieldval: string | Date;
    if (this.model?.templates != null) {
      this.model.templates.forEach((templateData) => {
        if (templateData.attributeName === fieldname) {
          if (templateData.attributeValueDataType === 'Date') {
            fieldval = new Date(templateData.attributeValue);
          } else {
            fieldval = templateData.attributeValue;
          }
          return fieldval;
        }
      });
    }
    return fieldval;
  }

  isTemplateField(templateName: string): boolean {
    let isTemplate = false;
    if (this.model?.templates != null) {
      this.model.templates.forEach((templateData) => {
        if (templateData.attributeName === templateName) {
          isTemplate = true;
          return isTemplate;
        }
      });
    }
    return isTemplate;
  }

  /* Cross Page Messaging */
  resize(event) {
    window.parent.postMessage('CommunityView:ResizeDialog', '*');
  }

  collapseSize(event){
    window.parent.postMessage('CommunityView:RemoveMin', '*');
  }

  /* Perform Search */
  performSearch() {
    if (this.processing || this.isSearchFormEmpty()) {
      return;
    }
    this.processing = true;
    this.uiBlock.emit(true);

    const valid = this.validateForm();
    if (!valid) {
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        summary: $localize`Not all fields are filled out correctly. Please check again.`,
        closable: false
      });
      this.processing = false;
      return;
    }

    const addressModel = new AddressModel();
    this.mapUiFieldsToModel(addressModel);
    this.editTermsText = this.getEditTermText();
    const searchFormAddressModel = addressModel;
    const refModel = convertAddressModeltoRefAddressModel(addressModel);
    this.apoSearchService.getLookupResultsFromAddressModel(refModel).subscribe(res => {
      this.searchResults = res;
      //Clearing selected search results before showing the lookup search results dialog
      this.searchResultsPanelComponent.selectedData = [];
      this.showSearchResults = true;
      this.uiBlock.emit(false);
      this.processing = false;
    });

  }

  backToSearch(){
    this.showSearchResults = false;
  }

  validateForm(): boolean {
    let valid = true;
    for (const simpleElemName in this.addressForm.controls) {
      if (Object.prototype.hasOwnProperty.call(this.addressForm.controls, simpleElemName)) {
        const formControl = this.addressForm.controls[simpleElemName] as FormControl;
        if (formControl.validator) {
          formControl.markAsDirty();
        }
        if (formControl.errors && Object.keys(formControl.errors).length > 0) {
          valid = false;
        }
      }
    }
    return valid;
  }

  isSearchFormEmpty(): boolean {
    const isEmpty = Object.values(this.addressForm.value).every(x => x === null || x === '');
    return isEmpty;
  }

  mapUiFieldsToModel(addressModel: AddressModel) {
    this.mapUItoModel(addressModel, 'country', 'ddlCountry');
    this.mapUItoModel(addressModel, 'houseNumberStart', 'txtStreetNo');
    this.mapUItoModel(addressModel, 'houseFractionStart', 'txtStartFraction');
    this.mapUItoModel(addressModel, 'houseNumberEnd', 'txtStreetEnd');
    this.mapUItoModel(addressModel, 'houseFractionEnd', 'txtEndFraction');
    this.mapUItoModel(addressModel, 'streetDirection', 'ddlStreetDirection');
    this.mapUItoModel(addressModel, 'streetPrefix', 'txtPrefix');
    this.mapUItoModel(addressModel, 'streetName', 'txtStreetName');
    this.mapUItoModel(addressModel, 'streetSuffix', 'ddlStreetSuffix');
    this.mapUItoModel(addressModel, 'streetSuffixDirection', 'ddlStreetSuffixDirection');
    this.mapUItoModel(addressModel, 'unitType', 'ddlUnitType');
    this.mapUItoModel(addressModel, 'unitStart', 'txtUnitNo');
    this.mapUItoModel(addressModel, 'unitEnd', 'txtUnitEnd');
    this.mapUItoModel(addressModel, 'secondaryRoad', 'txtSecondaryRoad');
    this.mapUItoModel(addressModel, 'secondaryRoadNumber', 'txtSecondaryRoadNo');
    this.mapUItoModel(addressModel, 'neighborhoodPrefix', 'txtNeighborhoodP');
    this.mapUItoModel(addressModel, 'neighborhood', 'txtNeighborhood');
    this.mapUItoModel(addressModel, 'addressDescription', 'txtDescription');
    this.mapUItoModel(addressModel, 'distance', 'txtDistance');
    this.mapUItoModel(addressModel, 'XCoordinator', 'txtXCoordinator');
    this.mapUItoModel(addressModel, 'YCoordinator', 'txtYCoordinator');
    this.mapUItoModel(addressModel, 'inspectionDistrictPrefix', 'txtInspectionDP');
    this.mapUItoModel(addressModel, 'inspectionDistrict', 'txtInspectionD');
    this.mapUItoModel(addressModel, 'city', 'txtCity');
    this.mapUItoModel(addressModel, 'county', 'txtCounty');
    this.mapUItoModel(addressModel, 'state', 'txtState');
    this.mapUItoModel(addressModel, 'zip', 'txtZip');
    this.mapUItoModel(addressModel, 'fullAddress', 'txtStreetAddress');
    this.mapUItoModel(addressModel, 'addressLine1', 'txtAddressLine1');
    this.mapUItoModel(addressModel, 'addressLine2', 'txtAddressLine2');
    this.mapUItoModel(addressModel, 'addressType', 'ddlAddressTypeFlag');
    this.mapUItoModel(addressModel, 'addressTypeFlag', 'addressTypeFlag');
    this.mapUItoModel(addressModel, 'levelPrefix', 'txtLevelPrefix');
    this.mapUItoModel(addressModel, 'levelNumberStart', 'txtLevelNbrStart');
    this.mapUItoModel(addressModel, 'levelNumberEnd', 'txtLevelNbrEnd');
    this.mapUItoModel(addressModel, 'houseNumberAlphaStart', 'txtHouseAlphaStart');
    this.mapUItoModel(addressModel, 'houseNumberAlphaEnd', 'txtHouseAlphaEnd');
    this.mapUItoModel(addressModel, 'streetNameStart', 'txtStreetNameStart');
    this.mapUItoModel(addressModel, 'streetNameEnd', 'txtStreetNameEnd');
    this.mapUItoModel(addressModel, 'crossStreetNameStart', 'txtCrossStreetNameStart');
    this.mapUItoModel(addressModel, 'crossStreetNameEnd', 'txtCrossStreetNameEnd');
    this.mapUItoModel(addressModel, 'UID', 'txtAddressUID');

    //Add Address Mode
    if (this.originalAPOTemplatesdata !== null) {
      addressModel.templates = this.mapUItoTemplateAttributeModel(this.originalAPOTemplatesdata);  //Bind templates Data
    }

  }

  mapUItoModel(addressModel: AddressModel, property: string, controlName: string) {
    if (this.addressForm.controls[controlName]) {
      addressModel[property] = this.addressForm.controls[controlName].value;
    }
  }

  mapUItoTemplateAttributeModel(templatesData: TemplateAttributeModel[]): TemplateAttributeModel[] {
    if (templatesData == null) {
      return null;
    }
    templatesData.forEach((templateData, index) => {
      if (this.addressForm.controls[templateData.attributeName] && templateData.vchFlag === FlagEnum.Y) {
        if (this.addressForm.controls[templateData.attributeName].controlData.type === AccelaControlType.Date) {
          const dateVal = this.addressForm.controls[templateData.attributeName].value;
          templatesData[index]['attributeValue'] = (dateVal === null) ? null : this.datePipe.transform(dateVal, 'MM/dd/yy');
        } else {
          templatesData[index]['attributeValue'] = this.addressForm.controls[templateData.attributeName].value;
        }
      }
    });
    return templatesData;
  }

  getEditTermText() {
    let editTermsText = '';
    let addressControlData: AccelaControl;
    let name: string;
    let value: string;
    for (const key in this.addressForm.value) {
      if (this.addressForm.value[key] != null) {
        addressControlData = this.addressForm.controls[key].controlData;
        name = addressControlData.label;
        value = this.addressForm.value[key];
        if (addressControlData.type === AccelaControlType.Dropdown) {
          const dIndex = addressControlData.options.findIndex(x => x.value === value);
          if (dIndex > -1) {
            value = addressControlData.options[dIndex].label;
          }
        }
        if (addressControlData.type === AccelaControlType.Date) {
          value = this.datePipe.transform(value, 'MM/dd/yy');
        }
        if (value !== '') {
          editTermsText += ` <strong>${name.replace(':', '')}</strong> contains: <strong>${value}</strong> |`;
        }
      }
    }
    if(!isStringNullOrWhiteSpace(editTermsText)){
      editTermsText.substring(0,editTermsText.length-2);
    }

    return editTermsText;
  }

  addResultsFromSearch(event: Event){
    this.showSearchResults = false;
    this.saveChanges.emit(this.searchResultsPanelComponent.selectedData);
  }

  /* UI Operations */
  close(){
    this.closeDialog.emit(false);
  }

  cancelSearch(event: Event){
    this.showSearchResults = false;
  }

  /* Save Changes */
  saveChangesToModel() {
    this.saveChanges.emit([this.model]);
  }
}
